import { XIcon } from 'morpheus-ui';
import { useEffect, useRef, useState } from 'react';
import Modules from '../../../accessControl/Modules';
import { ProtectedComponent } from '../../../accessControl/ProtectedComponent';
import { formatTime } from '../../../commom/formatTime';
import { Button } from '../../atoms/Button';

interface IPodcastPlayer {
  title: string;
  uri: string;
  setPlayer: () => void;
}

function PodcastPlayer({ title, uri, setPlayer }: IPodcastPlayer) {
  const [play, setPlay] = useState(true);
  const [sound, setSound] = useState(true);
  const [volume, setVolume] = useState(10);
  const [currentTime, setCurrentTime] = useState(0);
  const [currentSpeed, setCurrentSpeed] = useState(1);
  const podcastRef = useRef<HTMLAudioElement>(null);

  const toggleAudio = () => {
    if (play) {
      podcastRef.current?.pause();
      setPlay(false);
    } else {
      podcastRef.current?.play();
      setPlay(true);
    }
  };

  const handleSeek = (event: any) => {
    if (podcastRef.current) {
      podcastRef.current.currentTime = parseInt(event.target.value);
      setCurrentTime(parseInt(event.target.value));
    }
  };

  const handleVolume = (event: any) => {
    if (podcastRef.current) {
      setVolume(event.target.volume);
      podcastRef.current.volume = event.target.value / 10;
    }
  };

  useEffect(() => {
    if (play) {
      const intervalId = setInterval(() => {
        if (podcastRef.current) {
          podcastRef.current.play();
          setCurrentTime(podcastRef.current.currentTime);
        }
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [play, handleSeek]);

  const toggleButton = play ? 'pause' : 'play_arrow';
  let currentTimePercentage = 0;
  if (podcastRef.current) {
    currentTimePercentage = (currentTime / podcastRef.current.duration) * 100;
  }

  const changeSpeed = () => {
    if (podcastRef.current) {
      switch (currentSpeed) {
        case 0.5:
          setCurrentSpeed(1);
          podcastRef.current.playbackRate = 1;
          break;
        case 1:
          setCurrentSpeed(1.5);
          podcastRef.current.playbackRate = 1.5;
          break;
        case 1.5:
          setCurrentSpeed(2);
          podcastRef.current.playbackRate = 2;
          break;
        case 2:
          setCurrentSpeed(2.5);
          podcastRef.current.playbackRate = 2.5;
          break;
        case 2.5:
          setCurrentSpeed(0.5);
          podcastRef.current.playbackRate = 0.5;
          break;
      }
    }
  };

  return (
    <ProtectedComponent.ModuleGuard
      key={title}
      modulesAllowed={[Modules.NAVEGACAO_LIVRE_DISCIPLINAS.ID]}
      allowModal={true}
    >
      <div className='fixed bottom-14 left-0 h-48 w-full px-4 py-14 lg:bottom-0'>
        <div className='relative flex h-full rounded-md bg-color-brand-primary p-4 shadow-level-4'>
          <div className='text-white flex-1'>
            <h5 className='font-poppins text-md font-bold text-[#fff]'>
              {title}
            </h5>
            <p className='font-noto text-sm text-[#fff]'>
              {formatTime(currentTime)} /{' '}
              {formatTime(podcastRef.current?.duration || 0)}
            </p>
          </div>
          {sound ? (
            <div className='flex items-center gap-xl'>
              {currentSpeed !== 1 ? (
                <p className='text-[#fff]'>
                  <strong>{currentSpeed}x</strong>
                </p>
              ) : null}
              <Button
                onlyIcon
                LeftIcon={toggleButton}
                iconType='material'
                className='text-brand-color-tertiary-pure'
                onClick={toggleAudio}
              />
              <Button
                onlyIcon
                LeftIcon='volume_up'
                iconType='material'
                onClick={() => setSound(!sound)}
              />
              <Button
                onlyIcon
                LeftIcon='slow_motion_video'
                iconType='material'
                onClick={changeSpeed}
              />
            </div>
          ) : (
            <div className='flex items-center gap-xl'>
              <Button
                onlyIcon
                LeftIcon='volume_up'
                iconType='material'
                onClick={() => setSound(!sound)}
              />
              <input
                type='range'
                max={10}
                value={volume}
                className='bg-tertiary-500 h-2 appearance-none rounded-md'
                onChange={(e) => handleVolume(e)}
              />
            </div>
          )}
          {/* abaixo, a barra de progresso */}
          <div className='fixed bottom-28 left-0 h-2 w-full px-4 lg:bottom-14'>
            <div className='bg-white h-full w-full rounded-b-md'>
              <div
                className='roundend-r-none bg-primary-900 h-full rounded-l-md rounded-t-none'
                style={{ width: `${currentTimePercentage}%` }}
              ></div>
            </div>
          </div>
        </div>
        <div className='fixed bottom-28 flex w-full lg:bottom-14'>
          <input
            type='range'
            className='bg-transparent ml-[-6px] h-2 flex-1 appearance-none opacity-0'
            max={podcastRef.current?.duration || 0}
            value={currentTime}
            onChange={(e) => handleSeek(e)}
          />
          <span className='w-6'></span>
        </div>
        <div className='m-auto mt-2 flex items-center justify-center text-center'>
          <Button
            onClick={setPlayer}
            LeftIcon={XIcon}
            iconSize='medium'
            iconType='svg'
            variant='text'
            className='text-sm'
            classNameIcon='fill-button-color-tertiary-default'
          >
            Fechar player
          </Button>
        </div>
        <audio ref={podcastRef} src={uri} />
      </div>
    </ProtectedComponent.ModuleGuard>
  );
}

export default PodcastPlayer;
